import { z, type ZodRawShape } from "zod";
import { zfd } from "zod-form-data";
import { extendZodWithOpenApi } from "zod-openapi";
import { apiLink, errorResponse, ind, jsonBody, privateApi, successResponse, tagPaths, unref } from "../util.ts";
import {
    billing_account_id,
    created_at,
    datetime,
    deleted_at,
    designated_pool_uuid,
    disk_uuid,
    host_pool_uuid,
    int,
    ip,
    ipv4,
    ipv6,
    mac,
    optional_uuid_param,
    replica_uuid,
    simpleSuccessResponse,
    updated_at,
    user_id,
    uuid,
    vm_uuid,
} from "./common.ts";
import * as params from "./params.ts";

extendZodWithOpenApi(z);

//#region Common Virtual Machine fields
export const disk_size_gb = int.describe("Disk size (GB)").min(1).openapi({ ref: "size_gb", example: 10 });
export const vcpu = int.describe("Number of vCPUs").min(1).openapi({ example: 2 });
export const memory = int.describe("Memory size (MB)").min(1).openapi({ example: 2048 });
export const username = z.string().describe("Username for login").openapi({ example: "neo" });
export const password = z.string().describe("Password for login").openapi({ example: "hunter2" });

const os_name = z.string().describe("Operating System name").openapi({ example: "debian" });
const os_version = z.string().describe("Operating System version").openapi({ example: "11" });

// TODO: Maybe needs to be .enum(["WINDOWS"])
const license_type = z.string().nullable().describe("OS license type").openapi({ example: "WINDOWS" });
export const BootFirmware = z
    .enum(["bios", "uefi"])
    .describe("Boot firmware used by the virtual machine: `bios` for legacy BIOS boot, `uefi` for OVMF/UEFI boot.")
    .openapi({ ref: "BootFirmware", example: "bios", default: "bios" });
//#endregion

//#region Virtual machine storage

const VirtualMachineStorageBase = z
    .object({
        uuid,

        created_at,
        updated_at: updated_at.optional(),

        type: z.string(), // Overridden with specific types below
        size: disk_size_gb,

        pool: z.string().describe("Unknown field, usually empty or n/a").openapi({ example: "n/a" }),
    })
    .openapi({ ref: "VirtualMachineStorageBase" });

/// Replicas
export const ReplicaType = z.enum(["snapshot", "backup"]).describe("Replica type").openapi({ ref: "ReplicaType" });
const VirtualMachineStorageReplica = VirtualMachineStorageBase.extend({
    master_uuid: uuid.describe("Master disk UUID"),
    type: ReplicaType,
})
    .describe("A backup or snapshot of a virtual machine disk")
    .openapi({ ref: "VirtualMachineStorageReplica" });

/// Regular Disks
export const DiskType = z.enum(["n/a"]).describe("Disk type").openapi({ ref: "DiskType" });

export const VirtualMachineStorage = VirtualMachineStorageBase.extend({
    type: DiskType,
    name: z.string().openapi({ example: "vda" }),

    primary: z.boolean().describe("This is the primary (ie. first, boot) disk"),
    shared: z.boolean(),
    replica: z.array(VirtualMachineStorageReplica).optional(),

    user_id,
}).openapi({ ref: "VirtualMachineStorage" });

//#endregion

//#region Virtual machine actions

export const VmStatusEnum = z
    .enum(["creating", "running", "stopping", "stopped", "deleted"])
    .describe("Virtual machine state")
    .openapi({
        example: "running",
        ref: "VmStatusEnum",
    });

const VirtualMachineFields = {
    uuid: vm_uuid,

    created_at,
    updated_at,

    description: z.string().describe("Virtual Machine description"),
    name: z.string().describe("Virtual Machine name").openapi({ example: "PLATO" }),

    status: VmStatusEnum,

    /// Ownership
    user_id: unref(user_id).describe("Creating user ID"),
    billing_account: billing_account_id,

    /// Pools
    current_pool_name: z.string().optional().describe("Current resource pool name"),
    current_pool_uuid: host_pool_uuid.optional().describe("Current resource pool ID"),

    // NB: These are only optional because of legacy data cleanliness issues, see WRN-319
    designated_pool_name: z.string().optional().describe("Designated resource pool name"),
    designated_pool_uuid: designated_pool_uuid.optional(),

    /// Resource configuration
    vcpu,
    mac,
    memory,
    nic_iftune: z
        .object({
            average_kbps: z.number(),
            burst_kb: z.number(),
            peak_kbps: z.number(),
        })
        .describe(`Network bandwidth limits ${privateApi}`)
        .optional(),
    /// Operating system
    os_name,
    os_version,
    license_type: license_type.optional(),
    boot_firmware: BootFirmware.optional(),
    username,

    /// Networking
    hostname: z.string().min(1).describe("Hostname"),
    private_ipv4: ipv4.describe("Private IPv4 address of the virtual machine"),
    public_ipv6: ipv6.optional().describe("Public IPv6 address"),

    /// Storage
    backup: z.boolean().describe("Backups enabled"),
    storage: z.array(VirtualMachineStorage),
} as const satisfies ZodRawShape;

export const VirtualMachine = z.object(VirtualMachineFields).openapi({ ref: "VirtualMachine" });

const snapshotDisclaimer = "Must be omitted when creating from a snapshot.";

const VmPublicKey = z
    .string()
    .describe(`SSH public key passed to cloud-init user data. ${snapshotDisclaimer}`)
    .openapi({ example: "ssh-rsa..." });

export const VmCreateBody = z
    .object({
        name: VirtualMachineFields.name,
        description: VirtualMachineFields.description,

        billing_account_id: zfd.numeric(billing_account_id.optional()),

        /// Operating system
        os_name,
        os_version,
        boot_firmware: BootFirmware.optional(),

        /// Setup and access
        username: username.optional().describe(`Username for the initial user account. ${snapshotDisclaimer}`),
        password: password.optional().describe(`Initial password for the specified username. ${snapshotDisclaimer}`),
        // public_key: VmPublicKey.optional(),
        public_keys: z.array(VmPublicKey).optional(),
        cloud_init: z
            .string()
            .optional()
            .describe(
                "Custom cloud-init user-data (valid YAML or JSON). Any keys provided will override platform-provided values. Setting `users` here will overwrite `username` and `password` parameters.",
            ),

        /// Resources
        ram: zfd.numeric(memory),
        vcpu: zfd.numeric(vcpu),

        /// Host pool
        designated_pool_uuid: VirtualMachineFields.designated_pool_uuid.describe(
            "Resource pool to allocate this VM into",
        ),

        /// Networking
        reserve_public_ip: z.coerce.boolean().optional().default(true).describe("Reserve a public IP address"),
        network_uuid: optional_uuid_param.nullable().describe("Network ID to attach"),

        /// Disks
        disk_uuid: optional_uuid_param.describe("Existing disk UUID to use as the VM's boot disk"),
        disks: zfd.numeric(
            unref(disk_size_gb).optional().describe("Disk size (GB). Required if `disk_uuid` is not set."),
        ),

        /// Snapshots
        // XXX: Use a separate VmCreate... type for starting from a snapshot?
        source_replica: optional_uuid_param.describe("Source virtual machine's disk snapshot ID"),
        source_uuid: optional_uuid_param.describe("Source virtual machine ID"),
    })
    .openapi({ ref: "VmCreateBody" });

export const VmModifyBody = z
    .object({
        uuid: vm_uuid,
        name: VirtualMachineFields.name.optional(),
        ram: zfd.numeric(memory.optional()),
        vcpu: zfd.numeric(vcpu.optional()),
        boot_firmware: BootFirmware.optional(),
    })
    .openapi({ ref: "VmModifyBody" });

export const VmStartBody = z.object({ uuid: vm_uuid }).openapi({ ref: "VmStartBody" });

export const VmRescueStartBody = z
    .object({ uuid: vm_uuid, rescue_image_name: z.string().optional() })
    .openapi({ ref: "VmRescueStartBody" });

export const VmReplicaCreateBody = z.object({ uuid: vm_uuid }).openapi({ ref: "VmReplicaCreateBody" });
export const VmReplicaDeleteBody = z
    .object({ replica_uuid, type: ReplicaType })
    .openapi({ ref: "VmReplicaDeleteBody" });
export const VmReplicaRebuildBody = z
    .object({
        replica_uuid,
        uuid: vm_uuid,
        type: ReplicaType,
    })
    .openapi({ ref: "VmReplicaRebuildBody" });

export const VmBackupEnableDisableBody = z.object({ uuid: vm_uuid }).openapi({ ref: "VmBackupEnableDisableBody" });

export const VmStopBody = z
    .object({ uuid: vm_uuid, force: z.boolean().describe("Stop forcefully") })
    .openapi({ ref: "VmStopBody" });

export const VmCloneBody = z.object({ uuid: vm_uuid, name: VirtualMachineFields.name }).openapi({ ref: "VmCloneBody" });

export const VmDeleteBody = z.object({ uuid: vm_uuid }).openapi({ ref: "VmDeleteBody" });

export const VmAddDiskBody = z
    .object({
        uuid: vm_uuid,
        size_gb: disk_size_gb,
    })
    .openapi({ ref: "VmAddDiskBody" });

export const VmResizeDiskBody = z
    .object({
        uuid: vm_uuid,
        disk_uuid,
        size_gb: disk_size_gb,
        check_vm_state: z.boolean().describe("Check VM state before resizing").optional(),
    })
    .openapi({ ref: "VmResizeDiskBody" });

export const VmDeleteDiskBody = z
    .object({ uuid: vm_uuid, storage_uuid: disk_uuid })
    .openapi({ ref: "VmDeleteDiskBody" });

export const VmChangePasswordBody = z
    .object({ uuid: vm_uuid, username, password })
    .openapi({ ref: "VmChangePasswordBody" });

export const VmStartInRescueBody = z.object({ uuid: vm_uuid }).openapi({ ref: "VmStartInRescueBody" });

export const VmStartCustomRescueRepository = z
    .enum(["platform", "private"])
    .describe("Where the boot image is loaded from: `platform` for built-in media, `private` for a bootable user disk.")
    .openapi({
        ref: "VmStartCustomRescueRepository",
    });

export const VmStartCustomRescueBody = z
    .object({
        uuid: vm_uuid,
        boot_image_uuid: uuid.describe(
            "Boot image UUID to attach for this start. Use a read-only bootable disk UUID when `boot_image_repository` is `private`.",
        ),
        boot_image_repository: VmStartCustomRescueRepository,
    })
    .openapi({ ref: "VmStartCustomRescueBody" });

export const VmReinstallBody = z
    .object({ uuid: vm_uuid, os_name: VirtualMachineFields.os_name, os_version: VirtualMachineFields.os_version })
    .openapi({ ref: "VmReinstallBody" });

export const VmChangeBillingAccountBody = z
    .object({
        uuid,
        billing_account_id,
        // Backend does not check this
        resource_type: z.enum(["vm"]).optional().describe("_unused_").openapi({ deprecated: true }),
    })
    .openapi({ ref: "ChangeResourceBillingAccountBody" });

export const VmListResponse = z.array(VirtualMachine);

//#endregion

//#region VM images
export const VmImageVersion = z
    .object({
        os_version: unref(os_version).describe("Version key appended to the image name prefix"),
        display_name: z.string(),
        published: z
            .boolean()
            .optional()
            .describe(
                "Whether this version is visible to non-admin users in the UI. Unpublished versions remain admin-only",
            ),
        boot_firmware: BootFirmware.optional(),
    })
    .openapi({ ref: "VmImageVersion" });

export const VmImageType = z
    .enum(["plain_os", "app_catalog"])
    .describe("OS image type: `plain_os` for regular OS images, `app_catalog` for application catalog images")
    .openapi({ ref: "VmImageType" });

export const VmImage = z
    .object({
        os_name: unref(os_name).describe("Stable image name prefix shared by all versions of this catalog entry"),
        display_name: z.string().describe("User-facing name shown when selecting an image"),
        ui_position: int.describe("Display order index used when listing images in the UI"),
        license_type: license_type.optional(),
        is_default: z.boolean().describe("Whether this image should be preselected in the UI"),
        is_app_catalog: z
            .boolean()
            .describe("Whether this entry belongs to the App Catalog instead of plain OS images"),
        icon: z
            .string()
            .describe("Base64-encoded SVG icon shown in the UI. Use an empty string when no icon is configured")
            .openapi({ contentEncoding: "base64" }),
        versions: z.array(VmImageVersion).describe("Available versions of this operating system"),
    })
    .openapi({ ref: "VmImage" });

export const VmImagesResponse = z.array(VmImage).openapi({ ref: "VmImagesResponse" });

export const VmCustomImage = z
    .object({
        uuid,
        image_name: z.string(),
        is_installation_media: z.boolean(),
        description: z.string(),
        is_published: z.boolean(),
    })
    .openapi({ ref: "VmCustomImage" });

export const VmCustomImagesResponse = z.array(VmCustomImage).openapi({ ref: "VmCustomImagesResponse" });

export const VmImageOrder = z
    .object({
        os_name,
        ui_position: int.describe("Display order index used when listing images in the UI"),
    })
    .openapi({ ref: "VmImageOrder" });

export const VmImageOrderResponse = z.array(VmImageOrder).openapi({ ref: "VmImageOrderResponse" });

//#endregion

const example_uuid = "123e4567-e89b-12d3-a456-426614174000";

//#region VM parameters

const VmParameterType = z.enum(["string", "integer"]).describe("Parameter type").openapi({ ref: "VmParameterType" });
const VmParameterConstraint = z
    .enum(["enum", "range", "regexp"])
    .describe("Parameter constraint type")
    .openapi({ ref: "VmParameterConstraint" });

const VmLimits = z.object({
    min: z.number().optional(),
    max: z.number().optional(),
});

export const VmParameter = z
    .object({
        parameter: z.string().describe("Parameter name"),
        description: z.string().describe("Parameter description"),
        mandatory: z.boolean(),
        type: VmParameterType,
        constraint: VmParameterConstraint.optional(),
        expression: z.string().optional(),
        depends_on: z.unknown().optional(),
        ignore_for: z.unknown().optional(),
        is_trashed: z.boolean().optional(), // XXX: what is this rubbish?
        limited_by: z.string().optional(),
        limits: z
            .array(
                z
                    .object({
                        os_name: z.string(),
                        values: z.array(z.string()).optional(),
                        mandatory: z.boolean().optional(),
                        description: z.string().optional(),
                    })
                    .merge(VmLimits),
            )
            .optional(),
        values: z.array(z.string()).optional(),
    })
    .merge(VmLimits)
    .openapi({ ref: "VmParameter" });

export const VmParameterResponse = z.array(VmParameter).openapi({ ref: "VmParameterResponse" });

//#endregion

//#region VM interfaces
const network_id = int.describe("Network ID");
const vmi_id = int.describe("VM interface ID");

const InterfaceIp = z
    .object({
        id: z.number(),
        address: ip,
        user_id,
        type: z.string(),
        network_id,
        enabled: z.boolean(),
        created_at: datetime,
        updated_at: datetime,
        unassigned_at: datetime.optional(),
        uuid,
        is_deleted: z.boolean(),
        is_ipv6: z.boolean(),
        // NB: all network interfaces has those props
        billing_account_id: billing_account_id.optional(),
        deleted_at,
    })
    .openapi({ ref: "InterfaceIp" });

const NetworkInfo = z
    .object({
        subnet: z.string(),
        name: z.string(),
        created_at: datetime,
        updated_at: datetime,
        uuid,
        type: z.string(),
        is_default: z.boolean(),
        subnet_ipv6: z.string(),
    })
    .openapi({ ref: "NetworkInfo" });

const subinterface_uuid = uuid.describe("Subinterface UUID");
const vlan_tag = int.min(1).max(4094).describe("Subinterface VLAN tag");

export const SubInterfaceCreateResponse = z
    .object({
        id: z.number(),
        uuid: subinterface_uuid,
        created_at: datetime,
        vmi_id,
        vlan_tag,
        mac_addr: mac,
        network_id,
    })
    .openapi({ ref: "SubInterfaceCreateResponse" });

export const SubInterface = z
    .object({
        id: z.number(),
        uuid: subinterface_uuid,
        created_at: datetime,
        vmi_id,
        vlan_tag,
        mac_addr: mac,
        network_id,
        network: NetworkInfo,
        ip_address: InterfaceIp,
    })
    .openapi({ ref: "SubInterface" });

export const VmInterface = z
    .object({
        network: NetworkInfo,
        private_ip_address: InterfaceIp,
        public_ipv6_address: InterfaceIp.nullable().optional(),
        subinterfaces: z.array(SubInterface),
        vmi_uuid: uuid,
        vmi_mac: mac,
        vm_uuid,
    })
    .openapi({ ref: "VmInterface" });

export const VmInterfacesResponse = z.array(VmInterface).openapi({ ref: "VmInterfacesResponse" });

const if_uuid_path = uuid
    .describe("Host interface UUID (the VM interface the subinterface is attached to)")
    .openapi({ param: { name: "if_uuid", in: "path" } });

const network_uuid_query = uuid.describe("Target VPC UUID").openapi({ param: { name: "network_uuid", in: "query" } });

const vlan_tag_query = zfd.numeric(vlan_tag).openapi({ param: { name: "vlan_tag", in: "query" } });

const subinterface_uuid_path = subinterface_uuid.openapi({ param: { name: "subinterface_uuid", in: "path" } });

//#endregion

export const vmPaths = tagPaths("vm")({
    "/{location}/user-resource/vm/list": {
        get: {
            summary: "List VMs",
            parameters: [params.location, params.billing_account_id],
            responses: {
                ...successResponse(VmListResponse),
            },
        },
    },

    //#region Virtual machine actions
    "/{location}/user-resource/vm": {
        post: {
            summary: "Create VM",
            description: ind`
            \`billing_account_id\` is optional if using an API token that is restricted to one billing account.

            To create a VM with an empty disk, use the special values \`os_name=_custom\` and \`os_version=_\`, or alternatively omit \`os_name\` and \`os_version\`. The VM can then be booted from a custom installation ISO to install an OS, see [Boot a rescue or installation media](${apiLink("/{location}/user-resource/vm/boot_iso_media", "vm", "post")}).`,
            parameters: [params.location],
            requestBody: jsonBody(VmCreateBody),
            responses: {
                ...successResponse(VirtualMachine),
                ...errorResponse(
                    "Compute resources temporarily unavailable due to high demand. If possible, choose a different region or location.",
                ),
                ...errorResponse({
                    errors: {
                        password: "mandatory parameter 'password' missing",
                        username: "mandatory parameter 'username' missing",
                    },
                }),
            },
        },
        patch: {
            summary: "Update VM",
            description:
                "Change the name, or resize the RAM and vCPU amount of a virtual machine (only when the machine is in the stopped state).",
            parameters: [params.location],
            requestBody: jsonBody(VmModifyBody, {
                examples: {
                    "Update name": {
                        description: "Update the name of the virtual machine",
                        value: { uuid: example_uuid, name: "New name" },
                    },
                    Resize: {
                        description: "Change the RAM and vCPUs of the virtual machine",
                        value: { uuid: example_uuid, ram: 4096, vcpu: 2 },
                    },
                },
            }),
            responses: {
                ...successResponse(VirtualMachine),
                // TODO: Put proper error messages here
                ...errorResponse("Failed to change virtual machine size"),
            },
        },
        delete: {
            summary: "Delete VM",
            parameters: [params.location],
            requestBody: jsonBody(VmDeleteBody),
            responses: {
                ...simpleSuccessResponse,
            },
        },
    },

    "/{location}/user-resource/vm/start": {
        post: {
            summary: "Start VM",
            parameters: [params.location],
            requestBody: jsonBody(VmStartBody),
            responses: {
                ...successResponse(VirtualMachine),
            },
        },
    },

    "/{location}/user-resource/vm/stop": {
        post: {
            summary: "Stop VM",
            description: ind`
            Tries to stop the VM gracefully via ACPI shutdown signal. This gives the OS the opportunity to do a proper shutdown.

            If, for some reason, the OS does not respond to a normal shutdown signal, an optional \`force=true\` parameter can be specified. This will "pull the plug" immediately – it is the physical equivalent of cutting the power supply to a machine. This has the potential danger of corrupting data on the disk of the VM, so use it with caution.`,
            parameters: [params.location],
            requestBody: jsonBody(VmStopBody),
            responses: {
                ...successResponse(VirtualMachine),
            },
        },
    },

    "/{location}/user-resource/vm/clone": {
        post: {
            summary: "Clone VM",
            description: "Clone existing virtual machine",
            parameters: [params.location],
            requestBody: jsonBody(VmCloneBody),
            responses: {
                ...successResponse(VirtualMachine),
            },
        },
    },

    "/{location}/user-resource/vm/reinstall": {
        post: {
            summary: "Reinstall VM",
            description: ind`
            Discard virtual machine's current storage state and overwrite it with an OS base image.

            \`os_name\` and \`os_version\` are optional, the default is to reinstall the same OS version that the VM currently has.`,
            parameters: [params.location],
            requestBody: jsonBody(VmReinstallBody),
            responses: {
                ...successResponse(VirtualMachine),
            },
        },
    },

    "/{location}/user-resource/vm/user": {
        patch: {
            summary: "Change password",
            description: "Set a new password for an existing user on the virtual machine. The VM must be running.",
            parameters: [params.location],
            requestBody: jsonBody(VmChangePasswordBody),
            responses: {
                ...simpleSuccessResponse,
                ...errorResponse("VM must be running to change password"),
            },
        },
    },

    "/{location}/user-resource/resource_billing": {
        post: {
            summary: "Change billing account",
            description: "Change the billing account of a virtual machine.",
            parameters: [params.location],
            requestBody: jsonBody(VmChangeBillingAccountBody),
            responses: {
                ...successResponse(VirtualMachine),
            },
        },
    },

    "/{location}/user-resource/vm/rebuild": {
        post: {
            summary: "Restore VM from snapshot",
            description: "Discard virtual machine's storage and restore it from a snapshot.",
            parameters: [params.location],
            requestBody: jsonBody(VmReplicaRebuildBody),
            responses: {
                ...successResponse(VirtualMachine),
            },
        },
    },

    "/{location}/user-resource/vm/backup": {
        post: {
            summary: "Toggle auto backup",
            description: "Toggle automatic backups for virtual machine",
            parameters: [params.location],
            requestBody: jsonBody(VmBackupEnableDisableBody),
            responses: {
                ...successResponse(VirtualMachine),
            },
        },
    },

    "/{location}/user-resource/vm/replica": {
        post: {
            summary: "Create replica",
            description: "Create snapshot of a virtual machine",
            parameters: [params.location],
            requestBody: jsonBody(VmReplicaCreateBody),
            responses: {
                ...successResponse(VirtualMachineStorage),
            },
        },
        delete: {
            summary: "Delete replica",
            description: "Delete replica by uuid",
            parameters: [params.location],
            requestBody: jsonBody(VmReplicaDeleteBody),
            responses: {
                ...simpleSuccessResponse,
            },
        },
    },

    "/{location}/user-resource/vm/boot_iso_media": {
        post: {
            summary: "Boot a rescue or installation media",
            description: ind`
            Launches the VM with a "live" OS image attached as a cd-rom device and used for booting. The mode is enabled for a single launch only: next time this VM is started, it will follow a normal routine.

            A custom bootable media can be used for booting the VM by passing "private" as the \`boot_image_repository\` parameter. In that case, the \`boot_image_uuid\` must refer to a disk that has \`read_only_bootable\` attribute set as \`true\`. The disk is expected to contain a bootable ISO 9660 file system.

            See also: [List bootable media images](${apiLink("/config/boot_images", "parameters")}), [List disks](${apiLink("/{location}/storage/disks", "block_storage")})`,
            parameters: [params.location],
            requestBody: jsonBody(VmStartCustomRescueBody),
            responses: {
                ...successResponse(VirtualMachine),
            },
        },
    },
    //#endregion

    //#region VM images and parameters
    "/config/vm_images": {
        get: {
            summary: "List VM images",
            description: "All VM images and corresponding versions for the platform Compute section.",
            tags: ["parameters"],
            responses: {
                ...successResponse(VmImagesResponse),
            },
        },
    },

    "/config/vm_images/plain_os": {
        get: {
            summary: "Get plain OS images",
            description: "Plain virtual machine images and corresponding versions for the platform Compute section.",
            tags: ["parameters"],
            responses: {
                ...successResponse(VmImagesResponse),
            },
        },
    },

    "/config/vm_images/app_catalog": {
        get: {
            summary: "Get app catalog images",
            description: "App Catalog images and corresponding versions for the platform Compute section.",
            tags: ["parameters"],
            responses: {
                ...successResponse(VmImagesResponse),
            },
        },
    },

    "/config/boot_images": {
        get: {
            summary: "List bootable media images",
            description: "Bootable ISO images.",
            tags: ["parameters"],
            responses: {
                ...successResponse(VmCustomImagesResponse),
            },
        },
    },

    "/api/parameters/vm": {
        get: {
            summary: "Get VM parameter constraints",
            description: "Describes VM creation parameters and their expected and allowed values.",
            tags: ["parameters"],
            responses: {
                ...successResponse(VmParameterResponse),
            },
        },
    },
    //#endregion

    //#region VM storage
    "/{location}/user-resource/vm/storage": {
        post: {
            summary: "Add disk",
            description: "Create and add a disk device of specified capacity to a virtual machine.",
            parameters: [params.location],
            requestBody: jsonBody(VmAddDiskBody),
            responses: {
                ...successResponse(VirtualMachineStorage),
            },
        },
        patch: {
            summary: "Modify disk",
            description:
                "Modify virtual machine disk with the specified capacity. New disk size cannot be smaller than current size.",
            parameters: [params.location],
            requestBody: jsonBody(VmResizeDiskBody),
            responses: {
                ...successResponse(VirtualMachineStorage),
            },
        },
        delete: {
            summary: "Delete disk",
            description: ind`
            Disconnects and completely removes a disk device from a virtual machine. Data on that disk will be irrecoverably lost.

            Primary/boot disks cannot be deleted.`,
            parameters: [params.location],
            requestBody: jsonBody(VmDeleteDiskBody),
            responses: {
                ...simpleSuccessResponse,
                ...errorResponse("Cannot delete primary/boot disk"),
            },
        },
    },
    //#endregion

    //#region VM network interfaces
    "/{location}/network/virtual-machines/interfaces": {
        get: {
            summary: "Get all network interfaces",
            parameters: [params.location],
            responses: {
                ...successResponse(VmInterfacesResponse),
            },
        },
    },
    "/{location}/network/virtual-machines/{uuid}/interfaces": {
        get: {
            summary: "Get VM network interfaces",
            parameters: [params.location, params.uuid],
            responses: {
                ...successResponse(VmInterfacesResponse),
            },
        },
    },
    "/{location}/network/virtual-machines/{uuid}/interfaces/{if_uuid}/subinterfaces": {
        post: {
            summary: "Create VM network subinterface",
            description: "Creates a subinterface on the specified host interface.",
            parameters: [params.location, params.uuid, if_uuid_path, network_uuid_query, vlan_tag_query],
            responses: {
                ...successResponse(SubInterfaceCreateResponse),
                ...errorResponse("Network not found."),
            },
        },
    },
    "/{location}/network/virtual-machines/{uuid}/subinterfaces/{subinterface_uuid}": {
        delete: {
            summary: "Delete VM network subinterface",
            description:
                "Deletes a subinterface identified by UUID. The subinterface must belong to the VM identified by `uuid`.",
            parameters: [params.location, params.uuid, subinterface_uuid_path],
            responses: {
                ...successResponse(z.boolean()),
                ...errorResponse("VM UUID mismatch"),
            },
        },
    },
    //#endregion
});

// cspell:words OVMF
